// useRegionTilePrefetch — kick off background tile fetches for every open
// region so the Service Worker's gb-tiles CacheFirst rule captures them
// before learners walk into a signal-dead zone in the hills.
//
// Strategy:
//   For each open region, generate every Stadia tile XYZ inside
//   boundsSw/boundsNe at zooms 15-19 and fetch each one once. SW intercepts
//   and caches; we don't need to keep the body. mode: "no-cors" + cache:
//   "no-store" matches useAudioPrefetch's approach (Storage tiles don't
//   echo CORS headers; opaque responses are fine for SW caching).
//
// We rate-limit concurrent fetches so we don't blow up the network queue
// or the iOS PWA quota inspector chart in one burst.

import { useEffect } from "react";
import type { RegionDoc, WithId } from "@goodbye/firebase";

// 2026-06-02: trimmed from [15..19] to [15..17]. The old range generated
// ~2,380 tile requests PER DEVICE PER RUN for 三貂嶺 — z18/z19 alone were
// ~94% of that (~2,233 tiles). Combined with the SW tile cache cap being
// far below that working set (see sw.ts), the cache thrashed and every app
// load re-hit Stadia, burning a fresh free-tier key's monthly quota in ~1
// day. z15-17 covers street-level detail for offline orientation (~147
// tiles total); on-demand panning still fetches z18/19 live when a learner
// actually zooms in, and the (now large enough) SW cache keeps them.
const TILE_ZOOMS = [15, 16, 17] as const;
const MAX_CONCURRENT = 4;

function tileXYZ(
  boundsSw: readonly [number, number],
  boundsNe: readonly [number, number],
  zoom: number,
): Array<{ x: number; y: number; z: number }> {
  const [swLat, swLng] = boundsSw;
  const [neLat, neLng] = boundsNe;
  const n = Math.pow(2, zoom);
  const lonToX = (lng: number) => Math.floor(((lng + 180) / 360) * n);
  const latToY = (lat: number) =>
    Math.floor(
      ((1 -
        Math.log(
          Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180),
        ) /
          Math.PI) /
        2) *
        n,
    );
  const xMin = lonToX(swLng);
  const xMax = lonToX(neLng);
  // Latitude increases northward but tile y increases southward, so the
  // SW corner produces yMax and NE corner produces yMin.
  const yMax = latToY(swLat);
  const yMin = latToY(neLat);
  const out: Array<{ x: number; y: number; z: number }> = [];
  for (let x = xMin; x <= xMax; x++) {
    for (let y = yMin; y <= yMax; y++) {
      out.push({ x, y, z: zoom });
    }
  }
  return out;
}

function tileUrl(x: number, y: number, z: number, apiKey: string): string {
  return `https://tiles.stadiamaps.com/tiles/alidade_smooth/${z}/${x}/${y}.png?api_key=${apiKey}`;
}

async function fetchSequence(
  urls: string[],
  cancelledRef: { current: boolean },
): Promise<void> {
  let i = 0;
  async function worker(): Promise<void> {
    while (!cancelledRef.current && i < urls.length) {
      const url = urls[i++];
      try {
        await fetch(url, { mode: "no-cors", cache: "no-store" });
      } catch {
        /* offline or blocked — leave for next visit */
      }
    }
  }
  await Promise.all(Array.from({ length: MAX_CONCURRENT }, () => worker()));
}

/**
 * Prefetch tiles for every open region. Triggered lazily — the consumer
 * passes regions + a `enabled` flag (e.g., after a 2.5s delay on Home).
 */
export function useRegionTilePrefetch(
  regions: WithId<RegionDoc>[],
  stadiaApiKey: string,
  enabled: boolean,
): void {
  useEffect(() => {
    if (!enabled) return;
    if (typeof window === "undefined") return;
    if (regions.length === 0) return;

    const cancelledRef = { current: false };
    const urls: string[] = [];
    for (const r of regions) {
      if (!r.open) continue;
      // Only prefetch tiles for regions known to be in a signal-dead zone.
      // 三貂嶺 sits in a valley with patchy 4G; everywhere else on the
      // activity route has coverage, so prefetching their tiles wastes
      // iOS PWA quota and the user's data. Adjust this allowlist if a
      // new hill-side region is added.
      if (r.id !== "sandiaoling") continue;
      for (const z of TILE_ZOOMS) {
        for (const { x, y, z: tz } of tileXYZ(r.boundsSw, r.boundsNe, z)) {
          urls.push(tileUrl(x, y, tz, stadiaApiKey));
        }
      }
    }

    fetchSequence(urls, cancelledRef);

    return () => {
      cancelledRef.current = true;
    };
  }, [enabled, regions, stadiaApiKey]);
}
